import { RowStatus } from '@monorepo/shared/componentsV2/Table/types';
import {
  ColumnName,
  ColumnSet,
  ColumnType,
  ColumnValue,
  LimitColumnSet,
  QueryColumn,
  QueryResponse,
  ResultRow,
} from 'mapistry-shared';

export enum SpecialColumnType {
  LIMIT_COLUMN = 'limitColumn',
}

export interface LimitColumnValue {
  maxThreshold?: string;
  minThreshold?: string;
  isExceedance: boolean;
}
export type DisplayColumnValue = ColumnValue | LimitColumnValue;
export type DisplayColumnType = ColumnType | SpecialColumnType;
export type DisplayQueryColumn = Omit<QueryColumn, 'columnType'> & {
  columnType: ColumnType | SpecialColumnType;
};

export function isColumnReorderable(column: DisplayQueryColumn) {
  return !column.isTimeSeriesDate && !column.isHidden;
}

export type DisplayResultRow = Record<ColumnName, DisplayColumnValue> & {
  id: string;
  rowStatus?: RowStatus;
};
function isString(x: unknown): x is string {
  return typeof x === 'string';
}

export function isLimitThresholdColumn(
  columnName: ColumnName,
  limitColumnSets: LimitColumnSet[],
) {
  return limitColumnSets
    .flatMap((lcs) => [lcs.minThresholdColumnName, lcs.maxThresholdColumnName])
    .filter(isString)
    .includes(columnName);
}

/**
 * A column set represent a primary and dependant columns, e.g. log resource field and resource properties.
 * A display column in a column set is the one that will be used to show value in place of a parent column,
 * e.g. user will see resource name in log resource field column, and column "Resource.Name" will be hidden.
 */
function isADisplayColumnInAColumnSet(
  columnName: ColumnName,
  columnSets: ColumnSet[],
): boolean {
  return columnSets.some((cs) => cs.displayColumn === columnName);
}

/**
 * We are hiding id columns since they don't have user-friendly values,
 * an exception if a column is a parent ColumnSet column and has a display column.
 */
function isIdColumnWithoutColumnSetDisplay(
  column: QueryColumn,
  columnSets: ColumnSet[],
) {
  if (column.columnType !== ColumnType.FOREIGN_ID) return false;
  return !columnSets.some(
    (cs) => cs.parentColumn === column.columnName && cs.displayColumn,
  );
}

export function getDisplayColumnType(
  columnName: ColumnName,
  columnType: ColumnType,
  limitColumnSets: LimitColumnSet[],
): DisplayColumnType {
  if (limitColumnSets.some((lcs) => lcs.exceedanceColumnName === columnName)) {
    return SpecialColumnType.LIMIT_COLUMN;
  }
  return columnType;
}

export function getDisplayQueryColumns(
  queryColumns: QueryColumn[],
  limitColumnSets: LimitColumnSet[],
  columnSets: ColumnSet[],
): DisplayQueryColumn[] {
  return queryColumns
    .filter(
      (queryColumn) =>
        !isLimitThresholdColumn(queryColumn.columnName, limitColumnSets) &&
        !isADisplayColumnInAColumnSet(queryColumn.columnName, columnSets) &&
        !isIdColumnWithoutColumnSetDisplay(queryColumn, columnSets) &&
        !queryColumn.isSystemColumn,
    )
    .map((queryColumn) => ({
      ...queryColumn,
      columnType: getDisplayColumnType(
        queryColumn.columnName,
        queryColumn.columnType,
        limitColumnSets,
      ),
    }));
}

function getLimitColumnValues(
  limitColumnSets: LimitColumnSet[],
  resultRow: ResultRow,
) {
  return limitColumnSets.reduce((it, lcs) => {
    const isExceedance = resultRow[lcs.exceedanceColumnName] as boolean;
    const maxThreshold = lcs.maxThresholdColumnName
      ? (resultRow[lcs.maxThresholdColumnName] as string)
      : undefined;
    const minThreshold = lcs.minThresholdColumnName
      ? (resultRow[lcs.minThresholdColumnName] as string)
      : undefined;
    const limitColumnValue: LimitColumnValue = {
      isExceedance,
      maxThreshold,
      minThreshold,
    };
    return {
      ...it,
      [lcs.exceedanceColumnName]: limitColumnValue,
    };
  }, {} as Record<string, LimitColumnValue>);
}

export function mapResultRows(
  queryResponse: QueryResponse,
): DisplayResultRow[] {
  const {
    limitColumnSets,
    results: { data: resultRows },
  } = queryResponse;
  return resultRows.map((resultRow, idx): DisplayResultRow => {
    const limitColumnValues = getLimitColumnValues(limitColumnSets, resultRow);

    const isRowExceedance = Object.values(limitColumnValues).some(
      (lcv) => lcv.isExceedance,
    );
    const displayResultRow: DisplayResultRow = {
      id: `${idx}`,
      // We don't need to worry about collisions here bc all column values are upper snake cased
      ...resultRow,
      ...limitColumnValues,
    };
    if (isRowExceedance) {
      displayResultRow.rowStatus = RowStatus.ERROR;
    }
    return displayResultRow;
  });
}
